import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DateUtils } from "@allaihealth/util";
import { SessionService } from '../session.service.js';
import { HttpParams } from "@angular/common/http";
import { PageEvent } from '@angular/material/paginator';
import { SortEvent } from 'allai-admin-shared';
import { DateRange } from '@angular/material/datepicker';

/** Service that manages the appointments list filters to maintain their changes and reloading */
@Injectable({
	providedIn: 'root'
})
export class AppointmentsFilterService {

	/** The start date  */
	private _startDate: Date;

	/** The end date  */
	private _endDate: Date;

	/** Pager for list views */
	private _pageEvent: PageEvent;

	/** Global Search */
	private _globalSearch: string;

	/** Global Search */
	private _sortOrder: SortEvent;

	/** Subject for filter changes */
	private onNewFilterSubject: Subject<void> = new Subject<void>();

	constructor(private sessionService: SessionService) {
		//clear on login
		this.sessionService.onLogin.subscribe(() => this.clear());
	}

	/** Dispatch an event on a change */
	private setChanged(savePage?: boolean) {
		if(!savePage) {
			this._pageEvent = null;
		}
		this.onNewFilterSubject.next();
	}

	/** Clear all saved filters and {@link localStorage} */
	clear() {
		this._startDate = undefined;
		this._endDate = undefined;
	}

	/** The start date to show on the page (Default:today) */
	get startDate(): Date {
		if (!this._startDate) {
			this._startDate = new Date();
			this._startDate.setDate(this._startDate.getDate() - 180);
		}
		return this._startDate;
	}

	/** The end date to show on the page (Default:today) */
	get endDate(): Date {
		if (!this._endDate) {
			this._endDate = new Date();
			this._endDate.setDate(this._endDate.getDate() + 1);
		}
		return this._endDate;
	}

	/** The date range */
	set dateRange(dateRange: DateRange<Date>){
		if(dateRange.start && dateRange.end) {
			this._startDate = dateRange.start;
			this._endDate = dateRange.end;
			this.setChanged();
		}
	}

	set pageEvent(pageEvent: PageEvent) {
		this._pageEvent = pageEvent;
		this.setChanged(true);
	}

	get pageEvent(): PageEvent {
		if (!this._pageEvent) {
			this._pageEvent = {
				pageIndex: 0,
				pageSize: 10,
				length: 100
			}
		}
		return this._pageEvent;
	}

	set globalSearch(globalSearch: string) {
		this._globalSearch = globalSearch;
		this.setChanged();
	}

	get globalSearch(): string {
		if (!this._globalSearch) {
			this._globalSearch = '';
		}
		return this._globalSearch;
	}

	set sortOrder(sortOrder: SortEvent) {
		this._sortOrder = sortOrder;
		this.setChanged();
	}

	get sortOrder(): SortEvent {
		if (!this._sortOrder) {
			this._sortOrder = {
				active: '',
				direction: ''
			};
		}
		return this._sortOrder;
	}

	/** Dispatched for filter changes */
	get onNewFilter() {
		return this.onNewFilterSubject.asObservable();
	}

	/** Get the query parameters for a request to the backend server */
	get params(): HttpParams {
		let params: HttpParams = new HttpParams();

		const zone = this.sessionService.organization.time_zone;
		const start = DateUtils.setZonedStartEndDate(this.startDate, true, zone);
		const end = DateUtils.setZonedStartEndDate(this.endDate, false, zone);
		const pageEvent = this.pageEvent;
		const sort: SortEvent = this.sortOrder;

		params = params.set("start", start.toMillis());
		params = params.set("end", end.toMillis());
		params = params.set("pageIndex", pageEvent.pageIndex);
		params = params.set("pageSize", pageEvent.pageSize);
		params = params.set("globalSearch", this.globalSearch);
		params = params.set("orderBy", sort.active);
		params = params.set("direction", sort.direction);


		return params;
	}
}
